// CookieConsent — first-visit banner asking the user to choose which
// non-essential cookies they allow. Currently the platform only sets
// strictly-necessary cookies (the auth + refresh JWT cookies, which are
// exempt from consent under EU rules), but this banner is the disclosure
// layer for the analytics + marketing categories that will follow.
//
// The user's choice persists in localStorage under CONSENT_KEY:
//   - "all"      → analytics + marketing cookies allowed
//   - "essential" → only strictly-necessary cookies
//
// Code that wants to set a non-essential cookie or load a tracking script
// should check `getCookieConsent() === 'all'` before doing so.

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const CONSENT_KEY = 'go2fix:cookie-consent'

type ConsentChoice = 'all' | 'essential'

export function getCookieConsent(): ConsentChoice | null {
  if (typeof window === 'undefined') return null
  const v = window.localStorage.getItem(CONSENT_KEY)
  if (v === 'all' || v === 'essential') return v
  return null
}

export default function CookieConsent() {
  const { t, i18n } = useTranslation('common')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    setVisible(getCookieConsent() === null)
  }, [])

  const choose = (choice: ConsentChoice) => {
    try {
      window.localStorage.setItem(CONSENT_KEY, choice)
    } catch {
      // Storage may be disabled (private mode) — banner stays dismissed
      // for the session but will reappear next visit.
    }
    setVisible(false)
  }

  if (!visible) return null

  const privacyHref = i18n.language?.startsWith('en') ? '/en/privacy' : '/confidentialitate'

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('cookieConsent.aria')}
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center pb-[env(safe-area-inset-bottom,0px)]"
    >
      <div className="m-4 w-full max-w-3xl rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl sm:p-5">
        <p className="text-sm text-gray-700 sm:text-base">
          {t('cookieConsent.body')}{' '}
          <Link to={privacyHref} className="text-blue-600 underline">
            {t('cookieConsent.privacyLink')}
          </Link>
          .
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => choose('essential')}
            className="inline-flex min-h-11 items-center justify-center rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('cookieConsent.essentialOnly')}
          </button>
          <button
            type="button"
            onClick={() => choose('all')}
            className="inline-flex min-h-11 items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            {t('cookieConsent.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  )
}
